import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { User, Mail, Calendar, LogOut, Shield, Bell, Palette } from 'lucide-react';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  return (
    <div className="min-h-screen bg-[#1c1814] pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="profile">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-[#FAFAFA] mb-2">
            My <span className="text-[#D4AF37]">Profile</span>
          </h1>
          <p className="font-body text-[#FAFAFA]/60">
            Manage your account settings
          </p>
        </div>
        
        {/* Profile Card */}
        <Card className="card-finance mb-8" data-testid="profile-card">
          <CardContent className="pt-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <Avatar className="h-24 w-24 border-4 border-[#D4AF37]">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#D4AF37] text-[#1c1814] text-2xl font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-display text-2xl text-[#FAFAFA] mb-1">{user?.name}</h2>
                <p className="text-[#FAFAFA]/60 font-body mb-4">{user?.email}</p>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs uppercase tracking-wider">
                    Legacy Builder
                  </span>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs uppercase tracking-wider">
                    Paper Trader
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Account Info */}
        <Card className="card-finance mb-8" data-testid="account-info">
          <CardHeader className="border-b border-[#D4AF37]/20">
            <CardTitle className="font-display text-lg text-[#FAFAFA] flex items-center gap-2">
              <User className="h-5 w-5 text-[#D4AF37]" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#D4AF37]/10">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-[#FAFAFA]/50" />
                <span className="text-[#FAFAFA]/60">Full Name</span>
              </div>
              <span className="text-[#FAFAFA]">{user?.name}</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-[#D4AF37]/10">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#FAFAFA]/50" />
                <span className="text-[#FAFAFA]/60">Email</span>
              </div>
              <span className="text-[#FAFAFA]">{user?.email}</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-[#D4AF37]/10">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-[#FAFAFA]/50" />
                <span className="text-[#FAFAFA]/60">Member Since</span>
              </div>
              <span className="text-[#FAFAFA]">{formatDate(user?.created_at)}</span>
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-[#FAFAFA]/50" />
                <span className="text-[#FAFAFA]/60">Authentication</span>
              </div>
              <span className="text-[#D4AF37]">Google OAuth</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Quick Settings */}
        <Card className="card-finance mb-8" data-testid="quick-settings">
          <CardHeader className="border-b border-[#D4AF37]/20">
            <CardTitle className="font-display text-lg text-[#FAFAFA]">Quick Settings</CardTitle>
            <CardDescription className="text-[#FAFAFA]/50">
              Customize your experience
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#D4AF37]/10">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-[#FAFAFA]/50" />
                <div>
                  <p className="text-[#FAFAFA]">Notifications</p>
                  <p className="text-xs text-[#FAFAFA]/50">Get alerts for trades and consultations</p>
                </div>
              </div>
              <span className="text-emerald-400 text-sm">Enabled</span>
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Palette className="h-5 w-5 text-[#FAFAFA]/50" />
                <div>
                  <p className="text-[#FAFAFA]">Theme</p>
                  <p className="text-xs text-[#FAFAFA]/50">Adaptive based on section</p>
                </div>
              </div>
              <span className="text-[#D4AF37] text-sm">Adaptive</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Danger Zone */}
        <Card className="border-red-500/30 bg-[#1c1814]" data-testid="danger-zone">
          <CardHeader className="border-b border-red-500/20">
            <CardTitle className="font-display text-lg text-red-400">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#FAFAFA]">Sign Out</p>
                <p className="text-sm text-[#FAFAFA]/50">Sign out from your account</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
